import { AccountCircle, ArrowBack, ArrowForward, Person } from "@mui/icons-material";
import { Avatar, Button, Typography } from "@mui/material"
import { Outlet, useLocation, useNavigate, useNavigationType } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";

import AppBar from "@mui/material/AppBar"
import Box from "@mui/material/Box"
// import CssBaseline from "@mui/material/CssBaseline"
import Divider from "@mui/material/Divider"
import Drawer from "@mui/material/Drawer"
import IconButton from "@mui/material/IconButton"
import List from "@mui/material/List"
import ListItem from "@mui/material/ListItem"
import ListItemButton from "@mui/material/ListItemButton"
import ListItemIcon from "@mui/material/ListItemIcon"
import ListItemText from "@mui/material/ListItemText"
import { LoadingButton } from "@mui/lab"
import LoginIcon from "@mui/icons-material/Login"
import MenuIcon from "@mui/icons-material/Menu"
import Profile from "./components/Profile"
import Toolbar from "@mui/material/Toolbar"
import { auth } from "./firebase"
import { colors } from "./components/theme"
import { getData } from "./components/core/CreateEditOptions/getData"
import { routes } from "./routes"
import { useAuthState } from "react-firebase-hooks/auth"
import React from "react";
import { PopupService } from "react-ui-components/components/PopupService";

const drawerWidth = 240

const Root = function (props: any) {
  const [mobileOpen, setMobileOpen] = useState(false)
  const [isClosing, setIsClosing] = useState(false)
  const [user, loading] = useAuthState(auth)
  const [userInfo, setUserInfo] = useState<any>(null)
  const [filterRoutes, setFilterRoutes] = useState<any[]>([])
  const [historyStack, setHistoryStack] = useState<Array<{ path: string; title: string }>>([])
  const [historyIndex, setHistoryIndex] = useState(-1)
  const historyIndexRef = useRef(-1)

  useEffect(() => {
    async function loadUser() {
      if (user) {
        const idTokenResult = await user.getIdTokenResult()
        if (idTokenResult.claims.Admin) {
          getData({
            setData: (data: any) => {
              setUserInfo(data)
            },
            path: `users/${user.uid}`,
          } as any)
        }
      }

      setFilterRoutes(routes.filter((x) => !x.hidden).filter((x) => x.auth === !!user))
    }
    loadUser()
  }, [user])

  const navigate = useNavigate()
  const location = useLocation()
  const navigationType = useNavigationType()

  const resolveRouteTitle = useMemo(() => {
    const normalize = (value: string) => String(value || "").replace(/^\/+/, "").replace(/\/+$/, "")
    const escapeRegex = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
    const toRegex = (routeName: string) => {
      const pattern = normalize(routeName)
        .split("/")
        .map((segment) => (segment.startsWith(":") ? "[^/]+" : escapeRegex(segment)))
        .join("/")
      return new RegExp(`^${pattern}$`)
    }

    return (pathname: string) => {
      const normalizedPath = normalize(pathname)
      const match = routes.find((route) => toRegex(route.name).test(normalizedPath))
      return match?.title || pathname
    }
  }, [])

  useEffect(() => {
    const entry = { path: location.pathname, title: resolveRouteTitle(location.pathname) }

    setHistoryStack((prev) => {
      let nextStack = [...prev]
      let nextIndex = historyIndexRef.current

      if (navigationType === "PUSH") {
        nextStack = nextStack.slice(0, historyIndexRef.current + 1)
        nextStack.push(entry)
        nextIndex = nextStack.length - 1
      } else if (navigationType === "REPLACE") {
        if (nextIndex >= 0) {
          nextStack[nextIndex] = entry
        } else {
          nextStack = [entry]
          nextIndex = 0
        }
      } else {
        const existingIndex = nextStack.findIndex((item) => item.path === entry.path)
        if (existingIndex >= 0) {
          nextIndex = existingIndex
        } else {
          nextStack.push(entry)
          nextIndex = nextStack.length - 1
        }
      }

      historyIndexRef.current = nextIndex
      setHistoryIndex(nextIndex)
      return nextStack
    })
  }, [location.pathname, navigationType, resolveRouteTitle])
  const handleDrawerClose = () => {
    setIsClosing(true)
    setMobileOpen(false)
  }

  const handleDrawerTransitionEnd = () => {
    setIsClosing(false)
  }

  const handleDrawerToggle = () => {
    if (!isClosing) {
      setMobileOpen(!mobileOpen)
    }
  }

  const UserAvatar = () => {
    if (!user) {
      return <LoginIcon />
    }

    if (user?.isAnonymous) {
      return <Person />
    }
    return userInfo?.profileImageUrl ? (
      <Avatar alt="Profile Image" src={userInfo?.profileImageUrl} />
    ) : (
      <AccountCircle />
    )
  }
  const drawer = (
    <div className="flex flex-col h-full">
      <Toolbar>
        {user ? (
          <div className="flex w-full flex-col">
            <Button
              className="flex flex-row gap-1"
              onClick={() => PopupService.setPopup(<Profile />)}
            >
              <UserAvatar />
              <span className="flex-wrap leading-none">
                {userInfo?.name || userInfo?.email || userInfo?.phoneNumber}{" "}
                <span style={{ fontSize: 10, color: colors.textDim, lineHeight: 1 }}>
                  {userInfo?.name && userInfo?.email
                    ? userInfo?.email
                    : userInfo?.name && !userInfo?.email
                    ? userInfo?.phoneNumber
                    : ""}
                </span>
              </span>
            </Button>
          </div>
        ) : null}
      </Toolbar>
      <Divider />
      <List>
        {user ? (
          <ListItem>
            <div className="flex justify-end w-full sm:hidden">
              {/* <UsersCounter variant="text" left={false} size="small"></UsersCounter> */}
            </div>
          </ListItem>
        ) : null}

        {filterRoutes.map((item: any, i) => (
          <ListItem key={i} disablePadding>
            <ListItemButton>
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText
                primary={item.title}
                onClick={() => {
                  navigate(item.name)
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <div className="flex flex-col justify-end h-full m-5">{/* <LanguageSelector /> */}</div>
    </div>
  )

  if (loading) {
    return <LoadingButton />
  }

  const previousEntry = historyIndex > 0 ? historyStack[historyIndex - 1] : null
  const nextEntry = historyIndex >= 0 && historyIndex < historyStack.length - 1 ? historyStack[historyIndex + 1] : null

  return (
    <div>
      <Box sx={{ display: "flex" }}>
        <AppBar
          position="fixed"
          sx={{
            width: { sm: `calc(100% - ${drawerWidth}px)` },
            ml: { sm: `${drawerWidth}px` },
          }}
        >
          <Toolbar className="flex justify-between">
            <div className="flex items-center gap-2">
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{ mr: 1, display: { sm: "none" } }}
              >
                <MenuIcon />
              </IconButton>
              <Button
                color="inherit"
                size="small"
                startIcon={<ArrowBack />}
                disabled={!previousEntry}
                onClick={() => navigate(-1)}
              >
                {previousEntry ? `Back: ${previousEntry.title}` : "Back"}
              </Button>
              <Button
                color="inherit"
                size="small"
                endIcon={<ArrowForward />}
                disabled={!nextEntry}
                onClick={() => navigate(1)}
              >
                {nextEntry ? `Next: ${nextEntry.title}` : "Next"}
              </Button>
            </div>
            <img src={require("./images/logo.png")} width="100px" height={50} alt="WinFacil-Logo" />
          </Toolbar>
        </AppBar>
        <Box
          component="nav"
          sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
          aria-label="mailbox folders"
        >
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onTransitionEnd={handleDrawerTransitionEnd}
            onClose={handleDrawerClose}
            ModalProps={{
              keepMounted: true,
            }}
            sx={{
              display: { xs: "block", sm: "none" },
              "& .MuiDrawer-paper": { boxSizing: "border-box", width: drawerWidth },
            }}
          >
            {drawer}
          </Drawer>
          <Drawer
            variant="permanent"
            sx={{
              display: { xs: "none", sm: "block" },
              "& .MuiDrawer-paper": { boxSizing: "border-box", width: drawerWidth },
            }}
            open
          >
            {drawer}
          </Drawer>
        </Box>
        <Box
          component="main"
          sx={{ flexGrow: 1, p: 3, width: { sm: `calc(100% - ${drawerWidth}px)` } }}
        >
          <Toolbar />
          <Outlet />
          <Typography className="flex justify-center">Copyright (c) 2024 WinFacil</Typography>
        </Box>
      </Box>
    </div>
  )
}

export default Root
